import { readSetupProfile, selectSetupRule } from './checkout-setup-profile';
import utils from '@bigcommerce/stencil-utils';
import ProductDetailsBase, { optionChangeDecorator } from './product-details-base';
import 'foundation-sites/js/foundation/foundation';
import 'foundation-sites/js/foundation/foundation.reveal';
import ImageGallery from '../product/image-gallery';
import modalFactory, { alertModal, showAlertModal } from '../global/modal';
import { isEmpty, isPlainObject } from 'lodash';
import nod from '../common/nod';
import { announceInputErrorMessage } from '../common/utils/form-utils';
import forms from '../common/models/forms';
import { normalizeFormData } from './utils/api';
import { isBrowserIE, convertIntoArray } from './utils/ie-helpers';
import bannerUtils from './utils/banner-utils';
import currencySelector from '../global/currency-selector';

const SETUP_FEE_PRODUCT_ID = 182870;
const SETUP_FEE_STORAGE_KEY = 'canterberrySetupFeeLinks';

const normalizeSetupFeeSku = value => String(value || '').trim().toUpperCase();
const normalizeSetupFeeLinkEntry = entry => {
    if (typeof entry === 'string' || typeof entry === 'number') {
        const setupItemId = String(entry).trim();

        return setupItemId ? { setupItemId, sku: '' } : null;
    }

    if (!entry || typeof entry !== 'object') {
        return null;
    }

    const setupItemId = String(entry.setupItemId || entry.itemId || '').trim();

    if (!setupItemId) {
        return null;
    }

    return {
        setupItemId,
        sku: normalizeSetupFeeSku(entry.sku),
    };
};

const normalizeSetupFeeLinkMap = entries => {
    const normalized = {};

    Object.entries(entries || {}).forEach(([parentItemId, entry]) => {
        const normalizedEntry = normalizeSetupFeeLinkEntry(entry);

        if (!normalizedEntry) {
            return;
        }

        normalized[String(parentItemId)] = normalizedEntry;
    });

    return normalized;
};

const normalizeOptionLabel = value => (value || '')
    .replace(/\s+/g, ' ')
    .replace(/required/ig, '')
    .replace(/[:*]/g, '')
    .trim()
    .toLowerCase();

const labelIncludes = (label, fragment) => normalizeOptionLabel(label).includes(normalizeOptionLabel(fragment));

const readSetupFeeLinks = () => {
    try {
        const raw = window.localStorage.getItem(SETUP_FEE_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (error) {
        return {};
    }
};

const writeSetupFeeLinks = data => {
    try {
        window.localStorage.setItem(SETUP_FEE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        // Ignore storage write failures and continue normal cart behavior.
    }
};

export default class ProductDetails extends ProductDetailsBase {
    constructor($scope, context, productAttributesData = {}) {
        super($scope, context);

        this.addToCartPending = false;
        this.$overlay = $('[data-cart-item-add] .loadingOverlay');
        this.imageGallery = new ImageGallery($('[data-image-gallery]', this.$scope));
        this.imageGallery.init();
        this.listenQuantityChange();
        this.$swatchOptionMessage = $('.swatch-option-message');
        this.swatchInitMessageStorage = {};
        this.swatchGroupIdList = $('[id^="swatchGroup"]').map((_, group) => $(group).attr('id'));
        this.storeInitMessagesForSwatches();

        const $form = $('form[data-cart-item-add]', $scope);

        if ($form[0].checkValidity()) {
            this.updateProductDetailsData();
        } else {
            this.toggleWalletButtonsVisibility(false);
        }

        this.addToCartValidator = nod({
            submit: $form.find('input#form-action-addToCart'),
            tap: announceInputErrorMessage,
        });

        const $productOptionsElement = $('[data-product-option-change]', $form);
        const hasOptions = $productOptionsElement.html().trim().length;
        const hasDefaultOptions = $productOptionsElement.find('[data-default]').length;
        const $productSwatchGroup = $('[id*="attribute_swatch"]', $form);
        const $productSwatchLabels = $('.form-option-swatch', $form);
        const placeSwatchLabelImage = (_, label) => {
            const $optionImage = $('.form-option-expanded', $(label));
            const optionImageWidth = $optionImage.outerWidth();
            const extendedOptionImageOffsetLeft = 55;
            const { right } = label.getBoundingClientRect();
            const emptySpaceToScreenRightBorder = window.screen.width - right;
            const shiftValue = optionImageWidth - emptySpaceToScreenRightBorder;

            if (emptySpaceToScreenRightBorder < (optionImageWidth + extendedOptionImageOffsetLeft)) {
                $optionImage.css('left', `${shiftValue > 0 ? -shiftValue : shiftValue}px`);
            }
        };

        $(window).on('load', () => {
            this.registerAddToCartValidation();
            $.each($productSwatchLabels, placeSwatchLabelImage);
        });

        if (context.showSwatchNames) {
            this.$swatchOptionMessage.removeClass('u-hidden');

            $productSwatchGroup.on('change', ({ target }) => {
                const swatchGroupElement = target.parentNode.parentNode;

                this.showSwatchNameOnOption($(target), $(swatchGroupElement));
            });

            $.each($productSwatchGroup, (_, element) => {
                const swatchGroupElement = element.parentNode.parentNode;

                if ($(element).is(':checked')) this.showSwatchNameOnOption($(element), $(swatchGroupElement));
            });
        }

        $productOptionsElement.on('change', event => {
            this.productOptionsChanged(event);
            this.setProductVariant();
        });

        $form.on('submit', event => {
            if (this.addToCartPending) {
                event.preventDefault();
                return;
            }
            this.addToCartValidator.performCheck();

            if (this.addToCartValidator.areAll('valid')) {
                this.addProductToCart(event, $form[0]);
            } else {
                $form[0].dispatchEvent(new CustomEvent('cg:cart-add-failure', {
                    detail: { message: 'Please complete the required product options.' },
                }));
            }
        });

        // Update product attributes. Also update the initial view in case items are oos
        // or have default variant properties that change the view
        if ((isEmpty(productAttributesData) || hasDefaultOptions) && hasOptions) {
            const $productId = $('[name="product_id"]', $form).val();
            const optionChangeCallback = optionChangeDecorator.call(this, hasDefaultOptions);

            utils.api.productAttributes.optionChange($productId, $form.serialize(), 'products/bulk-discount-rates', optionChangeCallback);
        } else {
            this.updateProductAttributes(productAttributesData);
            bannerUtils.dispatchProductBannerEvent(productAttributesData);
        }

        $productOptionsElement.show();

        this.previewModal = modalFactory('#previewModal')[0];
    }

    registerAddToCartValidation() {
        this.addToCartValidator.add([{
            selector: '[data-quantity-change] > .form-input--incrementTotal',
            validate: (cb, val) => {
                const result = forms.numbersOnly(val);
                cb(result);
            },
            errorMessage: this.context.productQuantityErrorMessage,
        }]);

        return this.addToCartValidator;
    }

    storeInitMessagesForSwatches() {
        if (this.swatchGroupIdList.length && isEmpty(this.swatchInitMessageStorage)) {
            this.swatchGroupIdList.each((_, swatchGroupId) => {
                if (!this.swatchInitMessageStorage[swatchGroupId]) {
                    this.swatchInitMessageStorage[swatchGroupId] = $(`#${swatchGroupId} ~ .swatch-option-message`).text().trim();
                }
            });
        }
    }

    setProductVariant() {
        const unsatisfiedRequiredFields = [];
        const options = [];

        $.each($('[data-product-attribute]'), (index, value) => {
            const optionLabel = value.children[0].innerText;
            const optionTitle = optionLabel.split(':')[0].trim();
            const required = optionLabel.toLowerCase().includes('required');
            const type = value.getAttribute('data-product-attribute');

            if ((type === 'input-file' || type === 'input-text' || type === 'input-number') && value.querySelector('input').value === '' && required) {
                unsatisfiedRequiredFields.push(value);
            }

            if (type === 'textarea' && value.querySelector('textarea').value === '' && required) {
                unsatisfiedRequiredFields.push(value);
            }

            if (type === 'date') {
                const isSatisfied = Array.from(value.querySelectorAll('select')).every((select) => select.selectedIndex !== 0);

                if (isSatisfied) {
                    const dateString = Array.from(value.querySelectorAll('select')).map((x) => x.value).join('-');
                    options.push(`${optionTitle}:${dateString}`);

                    return;
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }

            if (type === 'set-select') {
                const select = value.querySelector('select');
                const selectedIndex = select.selectedIndex;

                if (selectedIndex !== 0) {
                    options.push(`${optionTitle}:${select.options[selectedIndex].innerText}`);

                    return;
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }

            if (type === 'set-rectangle' || type === 'set-radio' || type === 'swatch' || type === 'input-checkbox' || type === 'product-list') {
                const checked = value.querySelector(':checked');
                if (checked) {
                    const getSelectedOptionLabel = () => {
                        const productVariantslist = convertIntoArray(value.children);
                        const matchLabelForCheckedInput = inpt => inpt.dataset.productAttributeValue === checked.value;
                        return productVariantslist.filter(matchLabelForCheckedInput)[0];
                    };
                    if (type === 'set-rectangle' || type === 'set-radio' || type === 'product-list') {
                        const label = isBrowserIE ? getSelectedOptionLabel().innerText.trim() : checked.labels[0].innerText;
                        if (label) {
                            options.push(`${optionTitle}:${label}`);
                        }
                    }

                    if (type === 'swatch') {
                        const label = isBrowserIE ? getSelectedOptionLabel().children[0] : checked.labels[0].children[0];
                        if (label) {
                            options.push(`${optionTitle}:${label.title}`);
                        }
                    }

                    if (type === 'input-checkbox') {
                        options.push(`${optionTitle}:Yes`);
                    }

                    return;
                }

                if (type === 'input-checkbox') {
                    options.push(`${optionTitle}:No`);
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }
        });

        let productVariant = unsatisfiedRequiredFields.length === 0 ? options.sort().join(', ') : 'unsatisfied';
        const view = $('.productView');

        if (productVariant) {
            productVariant = productVariant === 'unsatisfied' ? '' : productVariant;
            if (view.attr('data-event-type')) {
                view.attr('data-product-variant', productVariant);
            } else {
                const productName = view.find('.productView-title')[0].innerText.replace(/"/g, '\\$&');
                const card = $(`[data-name="${productName}"]`);
                card.attr('data-product-variant', productVariant);
            }
        }
    }

    /**
     * Checks if the current window is being run inside an iframe
     * @returns {boolean}
     */
    isRunningInIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    /**
     *
     * Handle product options changes
     *
     */
    productOptionsChanged(event) {
        const $changedOption = $(event.target);
        const $form = $changedOption.parents('form');
        const productId = $('[name="product_id"]', $form).val();

        // Do not trigger an ajax request if it's a file or if the browser doesn't support FormData
        if ($changedOption.attr('type') === 'file' || window.FormData === undefined) {
            return;
        }

        utils.api.productAttributes.optionChange(productId, $form.serialize(), 'products/bulk-discount-rates', (err, response) => {
            const productAttributesData = response.data || {};
            const productAttributesContent = response.content || {};
            this.updateProductAttributes(productAttributesData);
            this.updateView(productAttributesData, productAttributesContent);
            this.updateProductDetailsData();
            bannerUtils.dispatchProductBannerEvent(productAttributesData);

            if (!this.checkIsQuickViewChild($form)) {
                const $context = $form.parents('.productView').find('.productView-info');
                modalFactory('[data-reveal]', { $context });
            }
        });
    }

    /**
     * if this setting is enabled in Page Builder
     * show name for swatch option
     */
    showSwatchNameOnOption($swatch, $swatchGroup) {
        const swatchName = $swatch.attr('aria-label');
        const activeSwatchGroupId = $swatchGroup.attr('aria-labelledby');
        const $swatchOptionMessage = $(`#${activeSwatchGroupId} ~ .swatch-option-message`);

        $('[data-option-value]', $swatchGroup).text(swatchName);
        $swatchOptionMessage.text(`${this.swatchInitMessageStorage[activeSwatchGroupId]} ${swatchName}`);
        this.setLiveRegionAttributes($swatchOptionMessage, 'status', 'assertive');
    }

    setLiveRegionAttributes($element, roleType, ariaLiveStatus) {
        $element.attr({
            role: roleType,
            'aria-live': ariaLiveStatus,
        });
    }

    checkIsQuickViewChild($element) {
        return !!$element.parents('.quickView').length;
    }

    showProductImage(image) {
        if (isPlainObject(image)) {
            const zoomImageUrl = utils.tools.imageSrcset.getSrcset(
                image.data,
                { '1x': this.context.zoomSize },
                /*
                    Should match zoom size used for data-zoom-image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
            );

            const mainImageUrl = utils.tools.imageSrcset.getSrcset(
                image.data,
                { '1x': this.context.productSize },
                /*
                    Should match fallback image size used for the main product image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
            );

            const mainImageSrcset = utils.tools.imageSrcset.getSrcset(image.data);

            this.imageGallery.setAlternateImage({
                mainImageUrl,
                zoomImageUrl,
                mainImageSrcset,
            });
        } else {
            this.imageGallery.restoreImage();
        }
    }

    /**
     *
     * Handle action when the shopper clicks on + / - for quantity
     *
     */
    listenQuantityChange() {
        this.$scope.on('click', '[data-quantity-change] button', event => {
            event.preventDefault();
            const $target = $(event.currentTarget);
            const viewModel = this.getViewModel(this.$scope);
            const $input = viewModel.quantity.$input;
            const quantityMin = parseInt($input.data('quantityMin'), 10);
            const quantityMax = parseInt($input.data('quantityMax'), 10);

            let qty = forms.numbersOnly($input.val()) ? parseInt($input.val(), 10) : quantityMin;
            // If action is incrementing
            if ($target.data('action') === 'inc') {
                qty = forms.validateIncreaseAgainstMaxBoundary(qty, quantityMax);
            } else if (qty > 1) {
                qty = forms.validateDecreaseAgainstMinBoundary(qty, quantityMin);
            }

            // update hidden input
            viewModel.quantity.$input.val(qty);
            // update text
            viewModel.quantity.$text.text(qty);
            // perform validation after updating product quantity
            this.addToCartValidator.performCheck();

            this.updateProductDetailsData();
        });

        // Prevent triggering quantity change when pressing enter
        this.$scope.on('keypress', '.form-input--incrementTotal', event => {
            // If the browser supports event.which, then use event.which, otherwise use event.keyCode
            const x = event.which || event.keyCode;
            if (x === 13) {
                // Prevent default
                event.preventDefault();
            }
        });

        this.$scope.on('keyup', '.form-input--incrementTotal', () => {
            this.updateProductDetailsData();
        });
    }

    /**
     *
     * Add a product to cart
     *
     */
    loadSetupProfile(form) {
        const field = form.querySelector('[name="product_id"]');
        return readSetupProfile(Number(field && field.value), this.context.graphql_token);
    }

    handleSetupPricingFailure(message) {
        showAlertModal(message || 'Setup pricing could not be confirmed. Please try again.');
    }

    addProductToCart(event, form) {
        if (window.FormData === undefined) return undefined;
        event.preventDefault();
        if (this.setupFeeNeedsReview) { this.handleSetupPricingFailure('Please review your cart setup fee before adding this item again.'); return Promise.resolve(false); }
        if (this.setupPolicyPending || this.addToCartPending) return Promise.resolve(false);
        this.setupPolicyPending = true;
        const button = $('#form-action-addToCart', $(event.target));
        button.prop('disabled', true);
        return this.loadSetupProfile(form).then(async profile => {
            let rule = null;
            if (profile) {
                const selected = {};
                new FormData(form).forEach((value, key) => {
                    const match = /^attribute\[(\d+)\]$/.exec(key);
                    if (match) selected[match[1]] = String(value);
                });
                const id = Number(form.querySelector('[name="product_id"]').value);
                const quantity = Number(form.querySelector('[name="qty[]"]').value);
                rule = selectSetupRule(profile, id, selected, quantity);
                const sku = this.getSetupFeeSku(form);
                const pricedInput = JSON.stringify([id, quantity, selected, sku]);
                const existing = normalizeSetupFeeLinkMap(readSetupFeeLinks()[String(this.context.cartId)] || {});
                const legacy = Object.keys(existing).map(key => existing[key]).filter(entry => entry.sku === sku);
                if (legacy.length) {
                    const stillPresent = await new Promise((resolve, reject) => {
                        utils.api.cart.getContent({ template: 'cart/preview' }, (error, html) => {
                            if (error) { reject(new Error('Your existing setup fee could not be confirmed. Please review your cart.')); return; }
                            const fragment = document.createElement('div');
                            fragment.innerHTML = html || '';
                            const cartIds = Array.from(fragment.querySelectorAll('[data-cart-itemid]')).map(node => node.getAttribute('data-cart-itemid'));
                            resolve(legacy.some(entry => cartIds.includes(entry.setupItemId)));
                        });
                    });
                    if (stillPresent) throw new Error('Please review the existing setup fee for this item in your cart before adding another decoration.');
                }
                const finalSelected = {};
                new FormData(form).forEach((value, key) => {
                    const match = /^attribute\[(\d+)\]$/.exec(key);
                    if (match) finalSelected[match[1]] = String(value);
                });
                const finalInput = JSON.stringify([Number(form.querySelector('[name="product_id"]').value), Number(form.querySelector('[name="qty[]"]').value), finalSelected, this.getSetupFeeSku(form)]);
                if (finalInput !== pricedInput) throw new Error('Your selection changed while pricing was checked. Please add the item again.');
            }
            this.setupPolicyPending = false;
            button.prop('disabled', false);
            return this.addProductToCartWithSetup(event, form, rule);
        }).catch(error => {
            this.setupPolicyPending = false;
            button.prop('disabled', false);
            this.handleSetupPricingFailure(error.message);
            form.dispatchEvent(new CustomEvent('cg:cart-add-failure', { detail: { message: 'Setup pricing could not be confirmed.' } }));
            return false;
        });
    }

    addProductToCartWithSetup(event, form, resolvedSetup = null) {
        const $addToCartBtn = $('#form-action-addToCart', $(event.target));
        const originalBtnVal = $addToCartBtn.val();
        const waitMessage = $addToCartBtn.data('waitMessage');
        const shouldAddSetupFee = resolvedSetup ? resolvedSetup.customer_setup_cents > 0 : this.shouldAddSetupFee(form);

        // Do not do AJAX if browser doesn't support FormData
        if (window.FormData === undefined) {
            return;
        }

        // Prevent default
        event.preventDefault();
        this.addToCartPending = true;
        form.dispatchEvent(new CustomEvent('cg:cart-add-start'));

        $addToCartBtn
            .val(waitMessage)
            .prop('disabled', true);

        this.$overlay.show();

        // Add item to cart
        utils.api.cart.itemAdd(normalizeFormData(new FormData(form)), (err, response) => {
            const finalize = finalResponse => {
                currencySelector(finalResponse.data.cart_id);
                const errorMessage = err || finalResponse.data.error;

                $addToCartBtn
                    .val(originalBtnVal)
                    .prop('disabled', false);

                this.$overlay.hide();

                if (errorMessage) {
                    this.addToCartPending = false;
                    form.dispatchEvent(new CustomEvent('cg:cart-add-failure', {
                        detail: { message: 'The item could not be added. Please try again.' },
                    }));
                    const tmp = document.createElement('DIV');
                    tmp.innerHTML = errorMessage;

                    if (!this.checkIsQuickViewChild($addToCartBtn)) {
                        alertModal().$preModalFocusedEl = $addToCartBtn;
                    }

                    return showAlertModal(tmp.textContent || tmp.innerText);
                }

                this.addToCartPending = false;
                form.dispatchEvent(new CustomEvent('cg:cart-add-success'));

                if (this.previewModal) {
                    this.previewModal.open();

                    if (window.ApplePaySession) {
                        this.previewModal.$modal.addClass('apple-pay-supported');
                    }

                    if (!this.checkIsQuickViewChild($addToCartBtn)) {
                        this.previewModal.$preModalFocusedEl = $addToCartBtn;
                    }

                    this.updateCartContent(this.previewModal, response.data.cart_item.id);
                } else {
                    this.$overlay.show();
                    this.redirectTo(finalResponse.data.cart_item.cart_url || this.context.urls.cart);
                }
            };

            const primaryErrorMessage = err || response.data.error;

            if (primaryErrorMessage || !shouldAddSetupFee) {
                finalize(response);
                return;
            }

            const decorationIdentity = resolvedSetup ? Object.keys(resolvedSetup.selectors).sort().map(key => `${key}=${resolvedSetup.selectors[key]}`).join(',') : '';
            const selectedSku = this.getSetupFeeSku(form) + (resolvedSetup ? `::${decorationIdentity}::${resolvedSetup.fee_product_id}:${resolvedSetup.fee_selection.modifier_id}:${resolvedSetup.fee_selection.value_id}:${resolvedSetup.customer_setup_cents}` : '');

            this.findReusableSetupFeeItem(response.data.cart_id, selectedSku, (reusableSetupItemId, reuseError) => {
                if (reuseError && resolvedSetup) {
                    this.addToCartPending = false;
                    this.setupFeeNeedsReview = true;
                    $addToCartBtn.val(originalBtnVal).prop('disabled', false);
                    this.$overlay.hide();
                    form.dispatchEvent(new CustomEvent('cg:cart-add-failure', { detail: { message: 'Please review your cart setup fee.' } }));
                    this.handleSetupPricingFailure('The product was added, but the existing setup fee could not be confirmed. Please review your cart before continuing.');
                    return;
                }
                if (reusableSetupItemId) {
                    this.storeSetupFeeLink(response.data.cart_id, response.data.cart_item.id, reusableSetupItemId, selectedSku);
                    finalize(response);
                    return;
                }

                const setupFeePayload = normalizeFormData(new FormData());
                setupFeePayload.set('action', 'add');
                setupFeePayload.set('product_id', resolvedSetup ? resolvedSetup.fee_product_id : SETUP_FEE_PRODUCT_ID);
                setupFeePayload.set('qty[]', 1);
                if (resolvedSetup) {
                    setupFeePayload.set(`attribute[${resolvedSetup.fee_selection.modifier_id}]`, resolvedSetup.fee_selection.value_id);
                }

                utils.api.cart.itemAdd(setupFeePayload, (setupErr, setupResponse) => {
                    const setupErrorMessage = setupErr || setupResponse.data.error;

                    if (setupErrorMessage) {
                        if (resolvedSetup) {
                            this.addToCartPending = false;
                            this.setupFeeNeedsReview = true;
                            $addToCartBtn.val(originalBtnVal).prop('disabled', false);
                            this.$overlay.hide();
                            form.dispatchEvent(new CustomEvent('cg:cart-add-failure', { detail: { message: 'Please review your cart setup fee.' } }));
                            this.handleSetupPricingFailure('The product was added, but its setup fee could not be confirmed. Please review your cart before continuing.');
                            return;
                        }
                        finalize(response);
                        showAlertModal('The product was added, but the setup fee could not be added automatically. Please review the cart.');
                        return;
                    }

                    this.storeSetupFeeLink(setupResponse.data.cart_id, response.data.cart_item.id, setupResponse.data.cart_item.id, selectedSku);
                    finalize(setupResponse);
                });
            }, resolvedSetup ? resolvedSetup.fee_product_id : SETUP_FEE_PRODUCT_ID);
        });

        this.setLiveRegionAttributes($addToCartBtn.next(), 'status', 'polite');
    }

    shouldAddSetupFee(form) {
        const optionContainer = form.querySelector('[data-product-option-change]');

        if (!optionContainer) {
            return false;
        }

        const decorationField = Array.from(optionContainer.querySelectorAll('[data-product-attribute]')).find(field => {
            const label = field.querySelector('.form-label');
            return label && labelIncludes(label.textContent, 'decoration');
        });

        if (!decorationField) {
            return false;
        }

        const selectedValue = this.getSelectedAttributeValue(decorationField);
        const normalizedValue = normalizeOptionLabel(selectedValue);

        if (!normalizedValue) {
            return false;
        }

        return !normalizedValue.includes('no imprint');
    }

    getSelectedAttributeValue(field) {
        const type = field.getAttribute('data-product-attribute');

        if (type === 'set-select') {
            const select = field.querySelector('select');
            if (!select || select.selectedIndex <= 0) {
                return '';
            }

            return select.options[select.selectedIndex].textContent.trim();
        }

        const checked = field.querySelector('input:checked');

        if (!checked) {
            return '';
        }

        const label = checked.labels && checked.labels[0] ? checked.labels[0].textContent : '';
        return (label || '').trim();
    }

    getSetupFeeSku(form) {
        const viewModel = this.getViewModel(this.$scope);
        const viewSku = viewModel && viewModel.sku && viewModel.sku.$value
            ? normalizeSetupFeeSku(viewModel.sku.$value.text())
            : '';
        const formSkuField = form.querySelector('[name="sku"]');
        const formSku = formSkuField ? normalizeSetupFeeSku(formSkuField.value) : '';

        return viewSku || formSku;
    }

    findReusableSetupFeeItem(cartId, sku, onComplete, feeProductId = SETUP_FEE_PRODUCT_ID) {
        const normalizedSku = normalizeSetupFeeSku(sku);

        if (!cartId || !normalizedSku) {
            onComplete(null);
            return;
        }

        const allLinks = readSetupFeeLinks();
        const cartLinks = normalizeSetupFeeLinkMap(allLinks[String(cartId)] || {});
        const matchingEntry = Object.values(cartLinks).find(entry => entry.sku === normalizedSku && entry.setupItemId);

        if (!matchingEntry) {
            onComplete(null);
            return;
        }

        utils.api.cart.getContent({ template: 'cart/content' }, (err, response) => {
            if (err || !response) {
                onComplete(null, new Error('Cart setup verification failed.'));
                return;
            }

            const $content = $('<div />').html(response);
            const $setupRow = $content.find(`[data-item-row][data-cart-itemid='${matchingEntry.setupItemId}'][data-cart-productid='${feeProductId}']`);

            onComplete($setupRow.length ? matchingEntry.setupItemId : null);
        });
    }

    storeSetupFeeLink(cartId, parentCartItemId, setupCartItemId, sku = '') {
        if (!cartId || !parentCartItemId || !setupCartItemId) {
            return;
        }

        const links = readSetupFeeLinks();
        const cartKey = String(cartId);

        links[cartKey] = normalizeSetupFeeLinkMap(links[cartKey] || {});
        links[cartKey][String(parentCartItemId)] = {
            setupItemId: String(setupCartItemId),
            sku: normalizeSetupFeeSku(sku),
        };

        writeSetupFeeLinks(links);
    }

    /**
     * Get cart contents
     *
     * @param {String} cartItemId
     * @param {Function} onComplete
     */
    getCartContent(cartItemId, onComplete) {
        const options = {
            template: 'cart/preview',
            params: {
                suggest: cartItemId,
            },
            config: {
                cart: {
                    suggestions: {
                        limit: 4,
                    },
                },
            },
        };

        utils.api.cart.getContent(options, onComplete);
    }

    /**
     * Redirect to url
     *
     * @param {String} url
     */
    redirectTo(url) {
        if (this.isRunningInIframe() && !window.iframeSdk) {
            window.top.location = url;
        } else {
            window.location = url;
        }
    }

    /**
     * Update cart content
     *
     * @param {Modal} modal
     * @param {String} cartItemId
     * @param {Function} onComplete
     */
    updateCartContent(modal, cartItemId, onComplete) {
        this.getCartContent(cartItemId, (err, response) => {
            if (err) {
                return;
            }

            modal.updateContent(response);

            // Update cart counter
            const $body = $('body');
            const $cartQuantity = $('[data-cart-quantity]', modal.$content);
            const $cartCounter = $('.navUser-action .cart-count');
            const quantity = $cartQuantity.data('cartQuantity') || 0;
            const $promotionBanner = $('[data-promotion-banner]');
            const $backToShopppingBtn = $('.previewCartCheckout > [data-reveal-close]');
            const $modalCloseBtn = $('#previewModal > .modal-close');
            const bannerUpdateHandler = () => {
                const $productContainer = $('#main-content > .container');

                $productContainer.append('<div class="loadingOverlay pdp-update"></div>');
                $('.loadingOverlay.pdp-update', $productContainer).show();
                window.location.reload();
            };

            $cartCounter.addClass('cart-count--positive');
            $body.trigger('cart-quantity-update', quantity);

            if (onComplete) {
                onComplete(response);
            }

            if ($promotionBanner.length && $backToShopppingBtn.length) {
                $backToShopppingBtn.on('click', bannerUpdateHandler);
                $modalCloseBtn.on('click', bannerUpdateHandler);
            }
        });
    }

    /**
     * Hide or mark as unavailable out of stock attributes if enabled
     * @param  {Object} data Product attribute data
     */
    updateProductAttributes(data) {
        super.updateProductAttributes(data);
        this.showProductImage(data.image);
    }

    updateProductDetailsData() {
        const $form = $('form[data-cart-item-add]');
        const formDataItems = $form.serializeArray();

        const productDetails = {};

        for (const formDataItem of formDataItems) {
            const { name, value } = formDataItem;

            if (name === 'product_id') {
                productDetails.productId = Number(value);
            }

            if (name === 'qty[]') {
                productDetails.quantity = Number(value);
            }

            if (name.match(/attribute/)) {
                const productOption = {
                    optionId: Number(name.match(/\d+/g)[0]),
                    optionValue: value,
                };

                productDetails.optionSelections = productDetails?.optionSelections
                    ? [...productDetails.optionSelections, productOption]
                    : [productOption];
            }
        }

        document.dispatchEvent(new CustomEvent('onProductUpdate', {
            bubbles: true,
            detail: { productDetails },
        }));
    }
}
